"use client";

import { cn } from "@/lib/utils";
import { QuizView } from "./QuizView";
import { QuestionData } from "./QuizCard";

interface QuizPageProps {
  contents: { [key: string]: any };
  className?: string;
}

export function QuizPage({ contents, className }: QuizPageProps) {
  const cards = contents.questions as QuestionData[];

  if (!cards || cards.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-theme-1">
        No quiz questions available.
      </div>
    );
  }

  return (
    <div className={cn("h-full w-full overflow-y-auto pb-10", className)}>
      {/* Interactive quiz section */}
      <section className="mb-26">
        <QuizView questions={cards} />
      </section>

      {/* All questions header */}
      <div className="max-w-3xl mx-auto mb-6 px-6">
        <div className="flex items-center gap-3">
          <div className="h-5 w-1 rounded-full bg-brand-gradient" />
          <h2 className="text-lg font-semibold text-theme-0">
            All questions in this quiz ({cards.length})
          </h2>
        </div>
      </div>

      {/* All questions list */}
      <section className="max-w-3xl mx-auto px-6">
        <div className="grid gap-8">
          {cards.map((card, i) => (
            <div
              key={i}
              className={cn(
                "rounded-xl border border-theme",
                "bg-theme-0 backdrop-blur shadow-theme",
                "transition",
                "p-4"
              )}
            >
              <div className="flex flex-col gap-3">
                {/* Question */}
                <div className="text-sm text-theme-0 font-semibold">
                  {card.question}
                </div>

                {/* Options */}
                <div className="grid gap-1">
                  {card.options.map((option, idx) => {
                    const isCorrect = idx === card.correctAnswer;

                    return (
                      <div
                        key={idx}
                        className={cn(
                          "rounded-md px-3 py-2 text-sm border",
                          isCorrect
                            ? "border-green-500 bg-green-100 text-green-700"
                            : "border-theme bg-theme-1 text-theme-1"
                        )}
                      >
                        <span className="font-medium mr-1">
                          {String.fromCharCode(65 + idx)}.
                        </span>
                        {option}
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                <div className="text-sm text-theme-1 leading-relaxed">
                  {card.explanation}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
